import * as d3 from "d3";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { uDev, UDevGlobals } from "src/components/Planner/planner/uDev";
import {
  arrayIntersection,
  CMToPx,
  line_line_intersect2,
  P_xytonum,
  uMath,
  vectorRotationDegree,
  wallClosestPointToPoint,
  XYtoNumPoly
} from "src/components/Planner/planner/uMath";

const CURRENT_MATERIALS = {
  INNER_WALLS: {
    '2_4': 3,
    '5': 4
  },
  OUTER_WALLS: {
    '2_4': 12,
    '5': 20
  },
  WINDOWS: {
    '2_4': 2.5,
    '5': 7
  },
  INTERNAL_DOORS: {
    '2_4': 3.5,
    '5': 6.5
  },
  FRONT_DOORS: {
    '2_4': 16,
    '5': 28.5
  },
}

const ATTENUATIONS_V2 = {
  'wall_1': {
    'id': 'wall_1',
    'name': 'Concrete',
    '2_4': 12,
    '5': 20
  },
  'wall_2': {
    'id': 'wall_2',
    'name': 'Drywall',
    '2_4': 3,
    '5': 4
  },
  'wall_3': {
    'id': 'wall_3',
    'name': 'Brick',
    '2_4': 12,
    '5': 20
  },
  'window_1': {
    'id': 'window_1',
    'name': 'Glass',
    '2_4': 2.5,
    '5': 7
  },
  'window_2': {
    'id': 'window_2',
    'name': 'Double pane coated glass',
    '2_4': 13,
    '5': 20
  },
  'window_3': {
    'id': 'window_3',
    'name': 'Bulletproof glass',
    '2_4': 10,
    '5': 20
  },
  'door_1': {
    'id': 'door_1',
    'name': 'Wood',
    '2_4': 3.5,
    '5': 6.5
  },
  'door_2': {
    'id': 'door_2',
    'name': 'Steel',
    '2_4': 16,
    '5': 28.5
  },
}

const ATTENUATIONS = {
  WALLS: {
    Drywall: {
      '2_4': 3,
      '5': 4
    },
    Brick: {
      '2_4': 12,
      '5': 20
    },
    Concrete: {
      '2_4': 12,
      '5': 20
    },
  },

  WINDOWS: {
    Glass: {
      '2_4': 2.5,
      '5': 7
    },
    GlassDoublePaneCoated: {
      '2_4': 13,
      '5': 20
    },
    GlassBulletProof: {
      '2_4': 10,
      '5': 20
    }
  },

  DOORS: {
    Wood: {
      '2_4': 3.5,
      '5': 6.5
    },
    Steel: {
      '2_4': 16,
      '5': 28.5
    },
  }
}

const ACCESS_POINT_PNID = 11;

export const WIFI_BANDS = {
  "_24": "2_4",
  "_5": "5"
}

const EIRPs = {
  277: {
    [WIFI_BANDS._24]: 24,
    [WIFI_BANDS._5]: 22,
  },
  340: {
    [WIFI_BANDS._24]: 23,
    [WIFI_BANDS._5]: 26,
  },
  341: {
    [WIFI_BANDS._24]: 25,
    [WIFI_BANDS._5]: 25,
  },
  342: {
    [WIFI_BANDS._24]: 23,
    [WIFI_BANDS._5]: 26,
  },
  345: {
    [WIFI_BANDS._24]: 23,
    [WIFI_BANDS._5]: 26,
  },
  346: {
    [WIFI_BANDS._24]: 23,
    [WIFI_BANDS._5]: 26,
  },
  363: {
    [WIFI_BANDS._24]: 26,
    [WIFI_BANDS._5]: 26,
  },
  364: {
    [WIFI_BANDS._24]: 23,
    [WIFI_BANDS._5]: 23,
  },
};

export let WIFI_BAND = WIFI_BANDS._5;

export function setWifiBand(band) {
  WIFI_BAND = band;
}

export class AccessPoint {
  constructor(x, y, wifiBand = null) {
    this.x = x;
    this.y = y;
    this.MHz = 0;
    this.L0 = 0;
    this.EIRPdBm = 0; //dBm
    this.category = '';
    if (wifiBand === WIFI_BANDS._5) {
      this.set5GHz();
    }
    else {
      this.set2_4GHz();
    }
  }
  set5GHz() {
    this.MHz = 5300;
    this.EIRPdBm = 23;
    this.category = '5';
    this.recalcL0();
    return this;
  }
  set2_4GHz() {
    this.MHz = 2442;
    this.EIRPdBm = 20;
    this.category = '2_4';
    this.recalcL0();
    return this;
  }
  setGHz(GHz) {
    if (GHz == WIFI_BANDS._24) {
      this.set2_4GHz();
    }
    else if (GHz == WIFI_BANDS._5) {
      this.set5GHz();
    }
    return this;
  }
  setEIRP(dBm) {
    this.EIRPdBm = dBm;
  }
  recalcL0() {
    this.L0 = 20 * Math.log10(1) + 20 * Math.log10(this.MHz) - 27.55;
  }
}

export let WIFICALC = (function () {
  /**
   * @type {any[]}
   */
  var pts;
  /**
   * @type {number}
   */
  var increment;
  /**
   * @type {number}
   */
  var overshoot;
  /**
   * @type {boolean}
   */
  var inited = false;
  /**
   * @type {AccessPoint[]}
   */
  var APs = [];
  /**
   * @type {Wall[]}
   */
  var inner_walls_cache;
  function generateGridPoints() {
    var pts = [];
    var minX = 0 - overshoot;
    var maxX = 900 + overshoot;
    var minY = 0 - overshoot;
    var maxY = 900 + overshoot;
    for (var i = minX; i <= maxX; i += increment) {
      for (var j = minY; j <= maxY; j += increment) {
        pts.push({ x: i, y: j, intersectedWalls: [], distFromAP: [], Prxs: [] });
      }
    }
    return pts;
  }
  function drawPoints() {
    d3.select('#wificalc').remove();
    var wificalc = PlannerGlobals.container.append('g')
    .attr('id', 'wificalc')
    .selectAll('rect')
    .data(pts.filter(function (pt) { return !pt.noDraw }))
    .enter();

    wificalc.append('rect')
      .attr('x', function (d) { return d.x - increment / 2 })
      .attr('y', function (d) { return d.y - increment / 2 })
      .attr('width', increment)
      .attr('height', increment)
      .attr('fill', function (d) { return d.color ? d.color : 'white' })
      .attr('fill-opacity', 0.5)
      .attr('stroke-width', 0)
      .attr('data-Ldb', function (d) { return -d.value + 'dB'; });
  }

  function _20log10(num) {
    return 20 * Math.log10(num);
  }

  /**
   *
   * @param {Line} segment
   */
  function segmentWallIntersects(pt, AP) {
    var intersects = [];
    var segment = uMath.segmentFromPoints(pt, AP);
    for (var i = 0; i < inner_walls_cache.length; i++) {
      var wall = inner_walls_cache[i];
      var intersect = line_line_intersect2(segment, wall);
      if (intersect) {
        var distFromAP = uMath.pointDistance(AP, intersect) * PlannerGlobals.LENGTHOF1PX / 100;

        var intersectingMod = wall.WALLMOD_CACHE.find(function (wallMod) {
          return line_line_intersect2(segment, uMath.segmentFromWallMod(wallMod));
        });
        if (intersectingMod) {
          intersects.push({ wall: intersectingMod, intersect, distFromAP });
        }
        else {
          intersects.push({ wall, intersect, distFromAP });
        }
      }
    }
    intersects.sort(function (a, b) {
      return a.distFromAP < b.distFromAP ? -1 : 1;
    });

    var allowedPx = CMToPx(8);
    var idx = 0;
    while (idx + 1 < intersects.length) {
      var i1 = intersects[idx];
      var i2 = intersects[idx + 1];
      if (i1.wall.isOuterBorder || i2.wall.isOuterBorder) {
        idx += 2
      }
      else {
        var proj1 = wallClosestPointToPoint(i1.intersect, i2.wall);
        var proj2 = wallClosestPointToPoint(i2.intersect, i1.wall);
        var p1Len = uMath.pointDistance(i1.intersect, proj1);
        var p2Len = uMath.pointDistance(i2.intersect, proj2);

        if (p1Len < allowedPx && p2Len < allowedPx) {
          intersects.splice(idx, 2);
        }
        else {
          idx++;
        }
      }
    }

    return intersects;
  }

  function segmentWallIntersects_V2(pt, AP) {
    var intersects = [];
    var segment = uMath.segmentFromPoints(pt, AP);
    for (var i = 0; i < inner_walls_cache.length; i++) {
      var wall = inner_walls_cache[i];
      var intersect = line_line_intersect2(segment, wall);
      if (intersect) {
        var distFromAP = uMath.pointDistance(AP, intersect) * PlannerGlobals.LENGTHOF1PX / 100;
        intersects.push({ wall, intersect, distFromAP });
      }
    }
    intersects.sort(function (a, b) {
      return a.distFromAP < b.distFromAP ? -1 : 1;
    });

    var allowedPx = CMToPx(8);
    var idx = 0;
    while (idx + 1 < intersects.length) {
      var i1 = intersects[idx];
      var i2 = intersects[idx + 1];
      if (i1.wall.isOuterBorder || i2.wall.isOuterBorder) {
        idx += 2
      }
      else {
        var proj1 = wallClosestPointToPoint(i1.intersect, i2.wall);
        var proj2 = wallClosestPointToPoint(i2.intersect, i1.wall);
        var p1Len = uMath.pointDistance(i1.intersect, proj1);
        var p2Len = uMath.pointDistance(i2.intersect, proj2);

        if (p1Len < allowedPx && p2Len < allowedPx) {
          intersects.splice(idx, 2);
        }
        else {
          idx++;
        }
      }
    }

    return intersects;
  }

  /**
   * This is not working good somehow
   * @param {Point} pt
   * @param {AccessPoint} AP
   * @param {{wall: Wall, intersect: Point, distFromAP: number}[]} intersects
   */
  function Lw_bad(pt, AP, intersects) {
    var LiArr = [];
    var intersectPairs = [];
    // this is gonna be from closest to receiver (furthest from AP) -> furthest from receiver (closest to AP)
    for (var i = 0; i < intersects.length; i += 2) {
      intersectPairs.push([intersects[i], intersects[i + 1]]);
    }
    for (var i = 0; i < intersectPairs.length; i++) {
      var pair = intersectPairs[i];
      var lastPair = pair[1] || pair[0];
      var rot = vectorRotationDegree(AP, pt);
      var Lpt = uMath.pointDistance(uMath.pointAtAngleDistance(lastPair.intersect, rot, CMToPx(100)), AP) * PlannerGlobals.LENGTHOF1PX / 100;

      var L = _20log10(Lpt) + _20log10(AP.MHz) - 27.55;
      var L0 = AP.L0;
      var _20logAPd = _20log10(pt.distFromAP[AP.APidx]);

      var Li = L - L0 - _20logAPd - uMath.array.sumFromTo(LiArr, 0, i);

      LiArr.push(Li);
    }
    return uMath.array.mean(LiArr);
  }

  /**
   *
   * @param {Point} pt
   * @param {AccessPoint} AP
   * @param {{wall: Wall, intersect: Point, distFromAP: number}[]} intersects
   */
  function Lw_thickness_based(pt, AP, intersects) {
    var LiArr = [];
    var intersectPairs = [];
    for (var i = 0; i < intersects.length; i += 2) {
      intersectPairs.push([intersects[i], intersects[i + 1]]);
    }
    for (var i = 0; i < intersectPairs.length; i++) {
      var Li;
      var pair = intersectPairs[i];
      if (pair[0] && pair[1]) {
        var dist = uMath.pointDistance(pair[0].intersect, pair[1].intersect) * PlannerGlobals.LENGTHOF1PX; //cm
        if (dist < 20) {
          Li = 3.4;
        }
        else {
          Li = 6.9;
        }
      }
      else {
        //return 100;
        Li = 5;
      }
      LiArr.push(Li);
    }
    return uMath.array.mean(LiArr);
  }

  function isPairTypeOf(pair, type1, type2) {
    return pair[0] && pair[0].wall.type == type1 && pair[1] && pair[1].wall.type == type2 ||
        pair[1] && pair[1].wall.type == type1 && pair[0] && pair[0].wall.type == type2;
  }

  function pairHasType(pair, type) {
    return pair[0] && pair[0].wall.type == type || pair[1] && pair[1].wall.type == type;
  }

  /**
   *
   * @param {Point} pt
   * @param {AccessPoint} AP
   * @param {{wall: Wall, intersect: Point, distFromAP: number}[]} intersects
   */
  function KwLw_material_based(pt, AP, intersects) {
    var LiArr = [];
    var intersectPairs = [];
    for (var i = 0; i < intersects.length; i += 2) {
      intersectPairs.push([intersects[i], intersects[i + 1]]);
    }
    for (var i = 0; i < intersectPairs.length; i++) {
      var Li;
      var pair = intersectPairs[i];
      if (pair[0] && pair[1]) {
        if (pairHasType(pair, 'door')) {
          if (pairHasType(pair, 'OUTER_BORDER')) {
            Li = CURRENT_MATERIALS['FRONT_DOORS'][AP.category];
          }
          else {
            Li = CURRENT_MATERIALS['INTERNAL_DOORS'][AP.category];
          }
        }
        else if (pairHasType(pair, 'window')) {
          Li = CURRENT_MATERIALS['WINDOWS'][AP.category];
        }
        // outer wall
        else if (pairHasType(pair, 'OUTER_BORDER')) {
          Li = CURRENT_MATERIALS['OUTER_WALLS'][AP.category];
        }
        // inner wall
        else {
          Li = CURRENT_MATERIALS['INNER_WALLS'][AP.category];
        }
      }
      else {
        if (pair[0].wall.type == 'OUTER_BORDER') {
          Li = CURRENT_MATERIALS['OUTER_WALLS'][AP.category];
        }
        else {
          Li = null;
        }
      }

      if (Li) {
        LiArr.push(Li);
      }
    }
    return uMath.array.mean(LiArr) * LiArr.length;
  }

  /**
   *
   * @param {Point} pt
   * @param {AccessPoint} AP
   * @param {{wall: Wall, intersect: Point, distFromAP: number}[]} intersects
   */
  function KwLw_material_based_V2(pt, AP, intersects) {
    var LiArr = [];
    var intersectPairs = [];
    for (var i = 0; i < intersects.length; i += 2) {
      intersectPairs.push([intersects[i], intersects[i + 1]]);
    }
    for (var i = 0; i < intersectPairs.length; i++) {
      var Li;
      var pair = intersectPairs[i];
      if (pair[0] && pair[1]) {
        const Li0 = ATTENUATIONS_V2[pair[0].wall.materialId][AP.category];
        const Li1 = ATTENUATIONS_V2[pair[1].wall.materialId][AP.category];
        Li = (Li0 + Li1) / 2;
      }
      else {
        Li = ATTENUATIONS_V2[pair[0].wall.materialId][AP.category];
      }

      if (Li) {
        LiArr.push(Li);
      }
    }
    return uMath.array.mean(LiArr) * LiArr.length;
  }

  function kwLw(pt, AP, intersects) {
    if (intersects.length) {
      // var _kw = Math.ceil(intersects.length / 2);
      // var _Lw = Lw_bad(pt, AP, intersects);
      // var _Lw = Lw_thickness_based(pt, AP, intersects);
      // var _Lw = 3;
      // return _kw * _Lw;
      return KwLw_material_based_V2(pt, AP, intersects);
    }
    else {
      return 0;
    }
  }

  /**
   *
   * @param {any[]} pts
   * @param {AccessPoint[]} AP
   */
  function processPtsForAP(AP) {

  }

  var colors = [
    [56, '#ff3333'],
    [54, '#ff381a'],
    [52, '#ff622e'],
    [50, '#ff711a'],
    [48, '#ff9524'],
    [45, '#ffb429'],
    [42, '#ffce2e'],
    [39, '#ffea29'],
    [35, '#f8ff1f'],
    [32, '#43ff14'],
    [25, '#24e9ff'],
    [22, '#881aff'],
  ];

  /**
   *
   * @param {any[]} pts
   * @param {AccessPoint[]} APs
   */
  function processPtsForAPs() {
    var outer_walls_cache = UDevGlobals.WALLS_CACHE;
    UDevGlobals.WALLS_CACHE = inner_walls_cache;
    for (var i = 0; i < APs.length; i++) {
      var AP = APs[i];
      AP.APidx = i;
      pts.forEach(function (pt) {
        var intersects = segmentWallIntersects_V2(pt, AP);
        pt.intersectedWalls[AP.APidx] = Math.ceil(intersects.length / 2);
        var dist = uMath.pointDistance(pt, AP) * PlannerGlobals.LENGTHOF1PX / 100;
        pt.distFromAP[AP.APidx] = dist;
        var Ldb = AP.L0 + _20log10(dist) + kwLw(pt, AP, intersects)
        pt.Prxs[AP.APidx] = AP.EIRPdBm - Ldb;
      });
    }

    pts.forEach(function (pt) {
      pt.maxPower = Math.max.apply(this, pt.Prxs);

      pt.value = uMath.normalizeInRange(pt.maxPower, -80, -10) * 100;
    });
    UDevGlobals.WALLS_CACHE = outer_walls_cache;
  }
  function start(_increment, _APs, _overshoot) {
    /* rebuildWallsCache(); */
    if (!_APs.length) {
      reset();
      return;
    }
    increment = _increment;
    overshoot = _overshoot;
    APs = _APs;

    if (PlannerGlobals.wallSegmentation) {
      inner_walls_cache = PlannerGlobals.wallSegmentation.getWallSegmentsWithMaterials();
    }
    else if (PlannerGlobals.wallsForWifiCalc) {
      inner_walls_cache = PlannerGlobals.wallsForWifiCalc;
    }
    pts = generateGridPoints();
    processPtsForAPs();
    inited = true;
  }

  function getPts() {
    return pts;
  }

  /**
   *
   * @param {number} threshold
   * @param {Room[]} inRooms
   */
  function getCoverage(threshold = 0, inRooms = PlannerGlobals.rooms) {
    var ptsInRooms = pts.filter(function (pt) {
      return inRooms.some(function (room) {
        return d3.polygonContains(XYtoNumPoly(room.poly), P_xytonum(pt));
      });
    });

    return ptsInRooms.filter(function (pt) {
      return pt.value > threshold;
    }).length / ptsInRooms.length;
  }

  function getNoCoveragePoints(threshold = 0, inRooms = PlannerGlobals.rooms) {
    var ptsInRooms = pts.filter(function (pt) {
      return inRooms.some(function (room) {
        return d3.polygonContains(XYtoNumPoly(room.poly), P_xytonum(pt));
      });
    });

    return ptsInRooms.filter(function (pt) {
      return pt.value <= threshold;
    });
  }

  function calc(_increment, _APs, _overshoot = 0) {
    if (!inited || increment != _increment) {
      start(_increment, _APs, _overshoot);
    }
    else {
      if (APs.length != _APs.length) {
        pts = generateGridPoints();
      }
      APs = _APs;
      processPtsForAPs();
    }
  }

  function reset() {
    inited = false;
    APs = [];
    pts = [];
    inner_walls_cache = null;
  }

  return {
    reset,
    calc,
    getPts,
    getCoverage,
    getNoCoveragePoints
  }
})();

/**
 * At least one of the IDs is of an AP
 * @param {number[]} pnidArr
 */
export function isAP(pnidArr) {
  var apPNIDs = Object.keys(EIRPs).map(elem => +elem);
  return arrayIntersection(pnidArr, apPNIDs).length != 0;
}

export function getAPs() {
  return PlannerGlobals.devices
  .filter(function (dev) {
    return /* !dev.ghost && */ isAP([dev.product_name_id]);
  });
}

export function calcWifiHeatmap(increment = 10, overshoot = 150) {
  var APs = getAPs()
  .map(function (dev) {
    var center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
    var ap = new AccessPoint(center.x, center.y);
    ap.setGHz(WIFI_BAND);
    ap.setEIRP(EIRPs[dev.product_name_id][WIFI_BAND]);
    return ap;
  });
  WIFICALC.calc(increment, APs, overshoot);
}

